import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * Pressing the tab you are already on goes back to that section's start
 * (revision item 31).
 *
 * `setView(next)` with the same value is a no-op in React, and every screen's
 * sub-nav had the same problem — so from inside a purchase order, pressing
 * *Purchase orders* did nothing and you had to bounce off another tab. The
 * papercut is real: the tab's name promises a place, and pressing it should
 * take you there whatever you were drilled into.
 *
 * One primitive for both levels: the provider carries a counter that the nav
 * bumps on **every** press of the already-current entry, and a screen calls
 * `useNavReset` with whatever "back to my start" means for it — close the
 * takeover, drop the selection, collapse the row. The counter is per section,
 * so re-pressing *Stock* never resets the register hidden behind it, and a
 * half-mapped import in a *different* tab keeps its work (§2.2-9).
 *
 * The register is deliberately not wired: its "start" mid-payment is a
 * question with a dangerous answer, and the store-name button in the top bar
 * is already the way home.
 */
export const NavResetContext = createContext(0);

export function useNavReset(reset: () => void): void {
  const epoch = useContext(NavResetContext);
  // The freshest closure, without making it an effect dependency — screens
  // pass inline functions, and re-running on every render would fire resets
  // that nobody pressed for.
  const resetRef = useRef(reset);
  resetRef.current = reset;
  const seen = useRef(epoch);

  useEffect(() => {
    if (epoch === seen.current) return;
    seen.current = epoch;
    resetRef.current();
  }, [epoch]);
}
